import { useState } from 'react'
import { Logo } from './Logo'
import { LANG_OPTIONS, useI18n } from '../lib/i18n'
import type { View } from '../App'

interface HeaderProps {
  view: View
  onNavigate: (v: View, labId?: string) => void
}

export function Header({ view, onNavigate }: HeaderProps) {
  const { lang, setLang, t } = useI18n()
  const [menuOpen, setMenuOpen] = useState(false)

  const navItems: { id: View; label: string }[] = [
    { id: 'home', label: t.nav.home },
    { id: 'method', label: t.nav.method },
    { id: 'paradigms', label: t.nav.paradigms },
    { id: 'cases', label: t.nav.cases },
    { id: 'lab', label: t.nav.lab },
    { id: 'demo', label: t.nav.demo },
    { id: 'playbook', label: t.nav.playbook },
  ]

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[color:var(--color-orion-deep)]/80 border-b border-[color:var(--color-orion-surface)]/40">
      <div className="mx-auto max-w-6xl px-6 sm:px-10 py-4 flex items-center justify-between gap-4 sm:gap-6">
        <button
          type="button"
          onClick={() => onNavigate('home')}
          className="text-[color:var(--color-orion-text)] flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity shrink-0"
          aria-label={t.header.logoLabel}
        >
          <Logo size={30} />
        </button>

        <nav className="hidden md:flex items-center gap-6 flex-1 justify-center">
          {navItems.map((item) => {
            const active = item.id === view
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.id)}
                aria-current={active ? 'page' : undefined}
                className={`relative mono text-[length:var(--text-small)] uppercase tracking-widest pb-1 transition-colors ${
                  active
                    ? 'text-[color:var(--color-orion-text)]'
                    : 'text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)]'
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[color:var(--color-orion-amber)] rounded-full" />
                )}
              </button>
            )
          })}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <div
            role="radiogroup"
            aria-label={t.langSwitch.ariaLabel}
            className="inline-flex items-center rounded-full border-faint p-0.5 bg-[color:var(--color-orion-deep)]/60"
          >
            {LANG_OPTIONS.map((opt) => {
              const active = opt.code === lang
              return (
                <button
                  key={opt.code}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setLang(opt.code)}
                  title={opt.native}
                  className={`mono text-[length:var(--text-micro)] px-2.5 py-1 rounded-full transition-all duration-150 ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium'
                      : 'text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)]'
                  }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
            aria-label={t.nav.menuAriaLabel}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="mono text-[14px]">{menuOpen ? '✕' : '☰'}</span>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-[color:var(--color-orion-surface)]/40 backdrop-blur-md bg-[color:var(--color-orion-deep)]/95">
          <div className="mx-auto max-w-6xl px-6 sm:px-10 py-3 flex flex-col gap-1">
            {navItems.map((item) => {
              const active = item.id === view
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onNavigate(item.id)
                    setMenuOpen(false)
                  }}
                  aria-current={active ? 'page' : undefined}
                  className={`text-left px-3 py-2 rounded-[var(--radius-sm)] mono text-[length:var(--text-small)] uppercase tracking-widest transition-colors ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 text-[color:var(--color-orion-amber)]'
                      : 'text-[color:var(--color-orion-haze)] hover:bg-[color:var(--color-orion-night)] hover:text-[color:var(--color-orion-text)]'
                  }`}
                >
                  {item.label}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </header>
  )
}
